/**
 * TacoMark — brand taco icon rendered inline so it can react to the city
 * accent and stroke color via CSS variables without a network request.
 *
 * v9 — Redesigned as a folded-shell silhouette (U-shape at bottom, fillings
 * cresting above the shell rim) so the icon reads as a taco instead of a
 * rounded blob with three patties (which previously read as a hamburger at
 * small sizes).
 */
import React from 'react';

interface TacoMarkProps {
  size?: number;
  /** Override the shell fill color. Defaults to the city accent. */
  color?: string;
  /** Override the stroke color. Defaults to a soft dark outline. */
  strokeColor?: string;
  className?: string;
  title?: string;
}

export function TacoMark({
  size = 28,
  color,
  strokeColor,
  className = '',
  title = 'Taco Street',
}: TacoMarkProps) {
  const shellFill = color ?? 'var(--ts-accent-primary, #F4A11C)';
  const stroke = strokeColor ?? 'rgba(0,0,0,0.35)';

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      role="img"
      aria-label={title}
      className={className}
      style={{ display: 'inline-block', flexShrink: 0 }}
    >
      <title>{title}</title>
      {/* Fillings cresting above the shell rim — drawn first so the shell
          overlaps them at the rim line. */}
      {/* lettuce */}
      <path d="M7 16 C 9 11, 13 11, 14 14 C 15 11, 17 11, 18 14 C 19 11, 23 11, 25 16 Z"
            fill="#7DAA6E" stroke={stroke} strokeWidth="0.8" strokeLinejoin="round" />
      {/* meat / tomato dome */}
      <path d="M10 16 C 12 13, 15 13, 16 15 C 17 13, 20 13, 22 16 Z"
            fill="#B5481E" stroke={stroke} strokeWidth="0.8" strokeLinejoin="round" />
      {/* white crema/onion dot */}
      <circle cx="16" cy="14.5" r="1.1" fill="#F5EDE0" />

      {/* Folded shell — U-shape at the bottom half of the canvas */}
      <path
        d="M4 16 C 4 22, 9 27, 16 27 C 23 27, 28 22, 28 16 L 25 16 C 25 21, 21 24, 16 24 C 11 24, 7 21, 7 16 Z"
        fill={shellFill}
        stroke={stroke}
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      {/* Subtle inner fold line for shell texture */}
      <path d="M16 24.2 L 16 27" stroke={stroke} strokeWidth="1" strokeLinecap="round" opacity="0.4" />
    </svg>
  );
}

export default TacoMark;
